import { useMutation } from '@tanstack/react-query';
import { Download, X } from 'lucide-react';
import { useState } from 'react';
import { downloadApiFile, getApiErrorMessage } from '../../lib/api';

function todayInPakistan() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: 'Asia/Karachi',
  }).formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((item) => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function DailyCheckinsExportDialog({ accessToken, filters, initialDate, onClose }: {
  accessToken: string | null;
  filters: string;
  initialDate: string;
  onClose: () => void;
}) {
  const [date, setDate] = useState(initialDate || todayInPakistan);
  const download = useMutation({
    mutationFn: () => {
      const params = new URLSearchParams(filters);
      for (const key of ['date_from', 'date_to', 'event_type', 'missing_checkout', 'review_status', 'validation_status', 'page', 'page_size', 'ordering']) {
        params.delete(key);
      }
      params.set('date', date);
      return downloadApiFile(`/attendance/review/daily-checkins/export/?${params}`, accessToken ? { accessToken } : {});
    },
    onSuccess: onClose,
  });
  return <div className="confirmation-backdrop" role="presentation">
    <form aria-labelledby="daily-checkins-export-title" aria-modal="true" className="confirmation-dialog attendance-export-dialog" role="dialog" onSubmit={(event) => { event.preventDefault(); if (date && !download.isPending) download.mutate(); }}>
      <button aria-label="Close export" className="icon-button confirmation-close" disabled={download.isPending} onClick={onClose} type="button"><X size={19} /></button>
      <h2 id="daily-checkins-export-title">Daily check-ins XLSX</h2>
      <p>One row per BA per store for the selected day in Pakistan time. Among recorded entries, the latest approved check-in is preferred; otherwise the latest captured check-in is used. Also includes currently accepted BAs in active campaigns without a check-in, even when no shift is scheduled. Stores without assigned BAs are excluded.</p>
      <p>Attendance status distinguishes approved presence, pending review, rejected check-ins, pending or expired evidence, no check-in yet, absence after the scheduled shift ends, and no shift scheduled. Missing check-ins have blank evidence and review cells. Workspace, search, client, agency, campaign, store and BA filters apply. Screen date range, event, validation and review filters are replaced to show the complete daily view.</p>
      <label>Check-in date<input autoFocus required onChange={(event) => { setDate(event.target.value); download.reset(); }} type="date" value={date} disabled={download.isPending} /></label>
      <p className="target-period-confirmation">Uses your organisation&rsquo;s saved columns from Settings, including the optional Attendance status column. Includes private selfie thumbnails only when Selfie is selected. Global exports without an organisation workspace use all columns. Share only with authorised recipients. Up to 500 BA/store entries per download; narrow the filters for larger days.</p>
      {download.isError ? <p className="form-error" role="alert">{getApiErrorMessage(download.error)}</p> : null}
      <div className="confirmation-actions">
        <button className="button secondary" disabled={download.isPending} onClick={onClose} type="button">Cancel</button>
        <button className="button primary" disabled={!date || download.isPending} type="submit"><Download size={16} />{download.isPending ? 'Preparing XLSX…' : 'Download XLSX'}</button>
      </div>
    </form>
  </div>;
}
